/**
 * =========================================================================================
 *     Copyright © 2025 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   Lakehouses.tsx
 *     Description     :   Lakehouses secondary dashboard page
 *     Author          :   Peter Dobler
 *     Created         :   2026-01-27
 *     Modified        :   2026-06-14 | Conformed to the Spec 197 design blueprint —
 *                         migrated off MUI (Container/Grid/Paper/Table/Chip/Tabs/Dialog)
 *                         onto the canonical Tailwind+token UI kit (PageLayout / Card /
 *                         DataTable / StatCard / Button / Input / Select / Badge / Modal).
 *                         Presentation only — data fetching, filters, and backup actions
 *                         are unchanged. The functional detail dialog now uses the kit
 *                         <Modal> (transparent-but-blocking, movable, no outside-close).
 *     Constitution    :   v1.19.0
 *     Feature         :   lakehouse-management
 * =========================================================================================
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Search,
  AlertTriangle,
  CheckCircle,
  Database,
  HardDrive,
  Filter,
  X,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useHasAccess } from '../../../hooks/useAccess';
import api from '../services/api';
import LakehouseDetailPage from './LakehouseDetailPage';
import LakehouseCatalog from './LakehouseCatalog';
import { humanBytes } from '../utils/entitySize';
import { LakehouseSizeCell } from '../components/common/LakehouseSizeCell';
import { LakehouseScheduleCell, type LakehouseSchedule } from '../components/common/LakehouseScheduleCell';
import { LakehouseBackupStatusCell, type LakehouseProtection } from '../components/common/LakehouseBackupStatusCell';
import { lakehouseHelp } from '../../../data/help/lakehouseHelp';
import {
  PageLayout,
  Card,
  DataTable,
  Button,
  Input,
  Select,
  StatCard,
  ErrorState,
  LoadingState,
  Modal,
  Extended,
  QuickGlance,
  AppTooltip,
  PageHelpPanel,
} from '../../../components/ui';
import type { DataTableColumn } from '../../../components/ui';
import { formatEastern } from '@/utils/dateFormatter';

// ============================================================================
// Types
// ============================================================================

export interface Lakehouse {
  id: string;
  fabric_lakehouse_id: string;
  workspace_id: string;
  workspace_name: string;
  name: string;
  description: string | null;
  is_backup_enabled: boolean;
  backup_frequency: string | null;
  schedule_time: string | null;
  schedule_days: string | null;
  retention_days: number | null;
  excluded_tables: string | null;
  configuration_updated_at: string | null;
  last_backup_at: string | null;
  next_scheduled_backup: string | null;
  storage_size_gb: number | null;
  size_bytes: number | null;
  table_count: number | null;
  /** Spec 286 T159 item 4: set when the last collection did not size the lakehouse; its size is then old. */
  size_measurement_error: string | null;
  sizeLastRecordedGb: number | null;
  sizeLastRecordedOn: string | null;
  protection_tier: string | null;
  /** Spec 286 T159 item 26: protection and active schedules on Spec 201's definitions (the route's protection map). */
  backupProtection: LakehouseSchedule & { protection: LakehouseProtection };
  created_at: string;
  updated_at: string;
}

interface LakehouseStats {
  total: number;
  backupEnabled: number;
  backupDisabled: number;
  /** Spec 286 T159 item 26: over the same lakehouses as total. */
  protectedCount: number;
  pausedCount: number;
  noPlanCount: number;
}

interface Pagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

// Spec 286 T159 item 26: protection (Spec 201) and the backup_enabled switch; schedule = has an active schedule.
type BackupFilter = 'all' | 'protected' | 'paused' | 'none' | 'off';
type ScheduleFilter = 'all' | 'scheduled' | 'none';
type LastBackupFilter = 'all' | 'backed_up' | 'never';

// ============================================================================
// Component
// ============================================================================

/** One empty list, so memos keyed on the rows stay stable before the first load. */
const NO_ROWS: Lakehouse[] = [];

const Lakehouses: React.FC = () => {
  const { canAccess } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Spec 285 (owner: gate ALL backup functionality): without the backup license this is a plain
  // inventory list — no Protection view, no backup columns, stats, filters or actions. A
  // ?view=protection / ?backup= deep link is ignored rather than rendering a refused surface.
  const canSeeBackup = useHasAccess('backup');

  // Spec 216 T029: the Lakehouse Catalog is now the "Protection" tab of this
  // list (/lakehouses?view=protection; the old /lakehouses/catalog redirects).
  const [view, setView] = useState<'list' | 'protection'>(
    searchParams.get('view') === 'protection' ? 'protection' : 'list'
  );

  // Modal state for entity detail view (Feature 116)
  const [selectedLakehouseId, setSelectedLakehouseId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [backupFilter, setBackupFilter] = useState<BackupFilter>(
    (searchParams.get('backup') as BackupFilter) || 'all'
  );
  const [scheduleFilter, setScheduleFilter] = useState<ScheduleFilter>('all');
  const [lastBackupFilter, setLastBackupFilter] = useState<LastBackupFilter>('all');
  const workspaceFilter = searchParams.get('workspace_id') || '';
  const effectiveView: 'list' | 'protection' = canSeeBackup ? view : 'list';
  const effectiveBackupFilter: BackupFilter = canSeeBackup ? backupFilter : 'all';

  // Spec 226 (FR-011): React Query keeps the rows of the current filters on screen through a refresh,
  // and a failed refresh shows the Refresh badge instead of replacing them. The full error view is for
  // a list that never loaded under these filters.
  const listQuery = useQuery({
    queryKey: ['lakehouses', 'list', searchQuery, effectiveBackupFilter, workspaceFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      // Small dataset: fetch ALL rows so the DataTable paginates the full set client-side.
      // The old server-side page of 25 was never navigable (the table only client-paginates
      // the rows it's handed), so the list silently showed 25 of 110 lakehouses.
      params.set('pageSize', '1000');

      if (searchQuery) params.set('search', searchQuery);
      if (effectiveBackupFilter === 'off') params.set('is_backup_enabled', 'false');
      else if (effectiveBackupFilter !== 'all') params.set('protection', effectiveBackupFilter);
      if (workspaceFilter) params.set('workspace_id', workspaceFilter);

      return api.get<{
        success: boolean;
        data: Lakehouse[];
        pagination: Pagination;
      }>(`/api/v1/lakehouses?${params.toString()}`);
    },
  });
  const statsQuery = useQuery({
    queryKey: ['lakehouses', 'stats'],
    queryFn: async () => (await api.get<{ success: boolean; data: LakehouseStats; }>('/api/v1/lakehouses/stats')).data,
  });
  const lakehouses = listQuery.data?.data ?? NO_ROWS;
  const stats = statsQuery.data ?? null;
  const loading = listQuery.isLoading;
  const error = listQuery.data || !listQuery.error
    ? null
    : listQuery.error instanceof Error ? listQuery.error.message : 'Failed to fetch lakehouses';

  // Get workspace name from the first lakehouse if filtering by workspace
  const filteredWorkspaceName = useMemo(() => {
    if (workspaceFilter && lakehouses.length > 0) {
      return lakehouses[0]?.workspace_name || 'Selected Workspace';
    }
    return null;
  }, [workspaceFilter, lakehouses]);

  const canManage = canAccess('admin');

  // Spec 226: reloads after an action or from Retry. The queries load on their own when the
  // filters change; the header's standard Refresh re-fetches both (refreshAll).
  const { refetch: refetchList } = listQuery;
  const { refetch: refetchStats } = statsQuery;
  const fetchLakehouses = useCallback(async () => { await refetchList(); }, [refetchList]);
  const refreshAll = useCallback(async () => {
    await Promise.all([refetchList({ throwOnError: true }), refetchStats({ throwOnError: true })]);
  }, [refetchList, refetchStats]);

  // Spec 201: per-entity enable/disable, deploy, and configure are retired — backup is
  // managed centrally in the Backup section. Removed handleToggleBackup/handleDeploy/handleConfigure.

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (effectiveBackupFilter !== 'all') params.set('backup', effectiveBackupFilter);
    if (workspaceFilter) params.set('workspace_id', workspaceFilter);
    if (effectiveView === 'protection') params.set('view', 'protection');
    setSearchParams(params);
  }, [searchQuery, effectiveBackupFilter, workspaceFilter, effectiveView, setSearchParams]);

  // Client-side filtering (Backup Status is applied server-side; Schedule + Last Backup here)
  const filteredLakehouses = useMemo(() => {
    return lakehouses.filter((l) => {
      if (!canSeeBackup) return true; // the backup filters are not shown without the license
      if (scheduleFilter !== 'all') {
        const scheduled = (l.backupProtection?.activeSchedules ?? 0) > 0;
        if (scheduleFilter === 'scheduled' ? !scheduled : scheduled) return false;
      }
      if (lastBackupFilter === 'backed_up' && !l.last_backup_at) return false;
      if (lastBackupFilter === 'never' && l.last_backup_at) return false;
      return true;
    });
  }, [lakehouses, scheduleFilter, lastBackupFilter, canSeeBackup]);

  // Total backup size (bytes) across the loaded lakehouses — prefer the fine-grained byte inventory,
  // fall back to the coarse GB column where inventory is missing.
  const totalBytes = useMemo(
    () =>
      lakehouses.reduce(
        (sum, l) =>
          sum + (l.size_bytes != null ? Number(l.size_bytes) : (Number(l.storage_size_gb) || 0) * 1024 ** 3),
        0
      ),
    [lakehouses]
  );
  // Spec 286 T159 item 4: how many of them the last collection did not size; the total counts their last known size.
  const notMeasuredCount = useMemo(() => lakehouses.filter((l) => l.size_measurement_error).length, [lakehouses]);

  // Summary stats
  const summaryStats = useMemo(() => {
    return {
      total: stats?.total || 0,
      protectedCount: stats?.protectedCount || 0,
      pausedCount: stats?.pausedCount || 0,
      noPlanCount: stats?.noPlanCount || 0,
    };
  }, [stats]);


  // Format date
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Never';
    return formatEastern(dateString);
  };

  // Close the detail modal (shared by ✕, Escape, and inner onClose)
  const handleDetailClose = useCallback((): void => {
    setIsModalOpen(false);
    setSelectedLakehouseId(null);
    fetchLakehouses();
  }, [fetchLakehouses]);

  // Table columns
  const columns: DataTableColumn<Lakehouse>[] = [
    {
      key: 'name',
      header: 'Lakehouse',
      width: '26%',
      render: (lakehouse) => (
        <div className="flex min-w-0 items-center gap-2">
          <div className={iconTileClass(canSeeBackup && lakehouse.is_backup_enabled)}>
            <Database
              className={`h-4 w-4 ${
                canSeeBackup && lakehouse.is_backup_enabled
                  ? 'text-[var(--color-status-success)]'
                  : 'text-[var(--color-text-secondary)]'
              }`}
            />
          </div>
          <div className="min-w-0">
            <div className="truncate font-medium text-[var(--color-text-primary)]" title={lakehouse.name}>
              {lakehouse.name}
            </div>
            {lakehouse.description && (
              <div
                className="truncate text-xs text-[var(--color-text-secondary)]"
                title={lakehouse.description}
              >
                {lakehouse.description}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'workspace_name',
      header: 'Workspace',
      width: '18%',
    },
    {
      key: 'is_backup_enabled',
      header: 'Backup Status',
      width: '14%',
      render: (lakehouse) => (
        <LakehouseBackupStatusCell
          protection={lakehouse.backupProtection?.protection ?? 'none'}
          backupEnabled={!!lakehouse.is_backup_enabled}
        />
      ),
    },
    {
      key: 'size_bytes',
      header: 'Size',
      width: '10%',
      align: 'right',
      render: (lakehouse) => (
        <LakehouseSizeCell
          sizeBytes={lakehouse.size_bytes}
          tableCount={lakehouse.table_count}
          sizeGb={lakehouse.storage_size_gb}
          sizeError={lakehouse.size_measurement_error}
          lastRecordedGb={lakehouse.sizeLastRecordedGb}
          lastRecordedOn={lakehouse.sizeLastRecordedOn}
        />
      ),
    },
    {
      key: 'backup_frequency',
      header: 'Schedule',
      width: '12%',
      render: (lakehouse) => (
        <LakehouseScheduleCell schedule={lakehouse.backupProtection} />
      ),
    },
    {
      key: 'last_backup_at',
      header: 'Last Backup',
      width: '14%',
      render: (lakehouse) => (
        <span className="text-[var(--color-text-secondary)]">{formatDate(lakehouse.last_backup_at)}</span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      width: '14%',
      render: () =>
        canManage ? (
          <div className="flex items-center justify-end gap-1">
            {/* Spec 201: backup is managed centrally — link to the Backup section
                instead of per-entity configure/enable/disable/deploy. */}
            <AppTooltip title="Manage backups in the Backup section">
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate('/backup/storage');
                }}
              >
                <Database className="h-4 w-4 text-[var(--color-accent)]" />
              </Button>
            </AppTooltip>
          </div>
        ) : (
          <span className="text-[var(--color-text-secondary)]">—</span>
        ),
    },
  ];
  const visibleColumns = canSeeBackup ? columns : columns.filter((c) => !BACKUP_COLUMN_KEYS.has(String(c.key)));

  return (
    <PageLayout
      title="Lakehouses"
      description={canSeeBackup ? 'Configure backup settings for Fabric lakehouses' : 'Fabric lakehouses in your estate'}
      icon={<Database className="h-6 w-6" />}
      onRefresh={refreshAll}
      isRefreshing={listQuery.isFetching || statsQuery.isFetching}
      lastFetchedAt={listQuery.dataUpdatedAt}
      refreshError={listQuery.error ?? statsQuery.error}
      actions={
        <>
          <PageHelpPanel content={lakehouseHelp} buttonSize="sm" />
        </>
      }
    >
      {/* View switcher: operational list vs the protection catalog (T029). Spec 285: the
          Protection view is backup-licensed, and a switcher with one view is not shown. */}
      {canSeeBackup && (
      <div className="mb-6 flex gap-1 border-b border-[var(--color-border)]">
        {([
          { key: 'list', label: 'List' },
          { key: 'protection', label: 'Protection' },
        ] as const).map((v) => (
          <button
            key={v.key}
            onClick={() => setView(v.key)}
            className={
              '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ' +
              (view === v.key
                ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                : 'border-transparent text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]')
            }
          >
            {v.label}
          </button>
        ))}
      </div>
      )}

      {effectiveView === 'protection' ? (
        <LakehouseCatalog embedded />
      ) : error && !loading ? (
        <ErrorState
          title="Failed to load lakehouses"
          message={error}
          onRetry={refreshAll}
        />
      ) : (
        <>
          {/* Workspace filter indicator */}
          {workspaceFilter && (
            <Card className="mb-6 flex flex-wrap items-center justify-between gap-3 border-[var(--color-accent)]/40 bg-[var(--color-accent)]/5">
              <div className="flex items-center gap-2 text-sm text-[var(--color-text-primary)]">
                <Filter className="h-4 w-4 text-[var(--color-accent)]" />
                <span>
                  Showing lakehouses filtered by workspace:{' '}
                  <strong>{filteredWorkspaceName || workspaceFilter}</strong>
                </span>
              </div>
              <Button
                size="sm"
                variant="ghost"
                leftIcon={<X className="h-4 w-4" />}
                onClick={() => navigate('/lakehouses')}
              >
                Clear Filter
              </Button>
            </Card>
          )}

          {/* Summary tiles */}
          <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatCard label="Total Lakehouses" value={summaryStats.total} />
            {canSeeBackup && (<>
            <StatCard
              label="Protected"
              value={summaryStats.protectedCount}
              subtext="covered by an active plan"
              valueClassName="text-[var(--color-status-success)]"
              icon={<CheckCircle className="h-4 w-4" />}
            />
            <StatCard
              label="Not protected"
              value={summaryStats.pausedCount + summaryStats.noPlanCount}
              subtext={`${summaryStats.pausedCount} plan paused · ${summaryStats.noPlanCount} no plan`}
              valueClassName={
                summaryStats.pausedCount + summaryStats.noPlanCount > 0 ? 'text-[var(--color-status-warning)]' : undefined
              }
              icon={summaryStats.pausedCount + summaryStats.noPlanCount > 0 ? <AlertTriangle className="h-4 w-4" /> : undefined}
            />
            </>)}
            <StatCard
              label="Total Size"
              value={humanBytes(totalBytes)}
              subtext={notMeasuredCount > 0 ? `${notMeasuredCount} not measured on the last collection, counted at their last known size` : undefined}
              valueClassName="text-[var(--color-accent)]"
              icon={<HardDrive className="h-4 w-4" />}
            />
          </div>

          <QuickGlance>
            <Card className="p-4 text-sm text-[var(--color-text-secondary)]">
              Showing the at-a-glance summary. Open this page on a larger screen for the filters and full table.
            </Card>
          </QuickGlance>

          <Extended>
          {/* Filters */}
          <Card className="mb-6">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-12 md:items-end">
              <div className="md:col-span-4">
                <Input
                  isFullWidth
                  placeholder="Search lakehouses..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  leftAddon={<Search className="h-4 w-4" />}
                />
              </div>
              {canSeeBackup && (<>
              <div className="md:col-span-3">
                <Select
                  isFullWidth
                  label="Backup Status"
                  value={backupFilter}
                  onChange={(e) => setBackupFilter(e.target.value as BackupFilter)}
                  options={[
                    { value: 'all', label: 'All Statuses' },
                    { value: 'protected', label: 'Protected' },
                    { value: 'paused', label: 'Plan paused' },
                    { value: 'none', label: 'No plan' },
                    { value: 'off', label: 'Backups off' },
                  ]}
                />
              </div>
              <div className="md:col-span-2">
                <Select
                  isFullWidth
                  label="Schedule"
                  value={scheduleFilter}
                  onChange={(e) => setScheduleFilter(e.target.value as ScheduleFilter)}
                  options={[
                    { value: 'all', label: 'All Schedules' },
                    { value: 'scheduled', label: 'Active schedule' },
                    { value: 'none', label: 'No active schedule' },
                  ]}
                />
              </div>
              <div className="md:col-span-3">
                <Select
                  isFullWidth
                  label="Last Backup"
                  value={lastBackupFilter}
                  onChange={(e) => setLastBackupFilter(e.target.value as LastBackupFilter)}
                  options={[
                    { value: 'all', label: 'Any' },
                    { value: 'backed_up', label: 'Has a backup' },
                    { value: 'never', label: 'Never backed up' },
                  ]}
                />
              </div>
              </>)}
            </div>
            <div className="mt-3 flex items-center justify-between">
              <span className="text-sm text-[var(--color-text-secondary)]">
                {workspaceFilter
                  ? `${filteredLakehouses.length} lakehouses in workspace`
                  : `${filteredLakehouses.length} of ${summaryStats.total} lakehouses`}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchQuery('');
                  setBackupFilter('all');
                  setScheduleFilter('all');
                  setLastBackupFilter('all');
                  navigate('/lakehouses');
                }}
                disabled={
                  !searchQuery &&
                  backupFilter === 'all' &&
                  scheduleFilter === 'all' &&
                  lastBackupFilter === 'all' &&
                  !workspaceFilter
                }
              >
                Clear Filters
              </Button>
            </div>
          </Card>

          {/* Table */}
          <Card padding="none">
            {loading ? (
              <div className="p-4">
                <LoadingState rows={6} />
              </div>
            ) : (
              <DataTable
                columns={visibleColumns}
                rows={filteredLakehouses}
                rowKey={(l) => l.id}
                emptyMessage="No lakehouses found — sync lakehouses from a workspace to get started."
                onRowClick={(l) => {
                  setSelectedLakehouseId(l.id);
                  setIsModalOpen(true);
                }}
                minWidth="52rem"
              />
            )}
          </Card>
          </Extended>
        </>
      )}

      {/* Functional lakehouse detail — backup activation, storage mapping, scheduling.
          Kit <Modal>: transparent-but-blocking backdrop, movable by the header,
          outside-click does NOT dismiss (close via ✕, Escape, or an inner action). */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleDetailClose}
        title="Lakehouse Detail"
        size="xl"
        className="p-0"
      >
        {selectedLakehouseId && (
          <LakehouseDetailPage embeddedId={selectedLakehouseId} onClose={handleDetailClose} />
        )}
      </Modal>
    </PageLayout>
  );
};

/** Spec 285: columns that only make sense with the backup license (status, schedule, last run, manage-backups action). */
const BACKUP_COLUMN_KEYS = new Set(['is_backup_enabled', 'backup_frequency', 'last_backup_at', 'actions']);

/** Icon tile background for the Lakehouse name cell (success vs neutral). */
function iconTileClass(enabled: boolean): string {
  return (
    'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded ' +
    (enabled ? 'bg-[var(--color-status-success)]/10' : 'bg-[var(--color-bg-subtle)]')
  );
}

export default Lakehouses;
